import type { Message, ChatProvider, ChatSession, ProvidersResponse } from "../types/chat.types";
import { supabase } from "@foundation/altzero_base/base/utils/supabaseClient";

// API configuration
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
const CHARLIE_CHAT_ENDPOINT = `${API_URL}/api/charlie-chat`;
const CHARLIE_CHAT_STREAM_ENDPOINT = `${CHARLIE_CHAT_ENDPOINT}/stream`;

async function charlieRequestHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`;
  }
  return headers;
}

// Session storage key
const CHAT_SESSION_KEY = "charlie_chat_session";

// Follow-up question marker used in LLM responses
const FOLLOWUP_MARKER = "<!-- FOLLOWUPS:";
const FOLLOWUP_END = "-->";

// Fetch available providers from backend
export const getProviders = async (): Promise<ProvidersResponse> => {
  try {
    const response = await fetch(`${CHARLIE_CHAT_ENDPOINT}/providers`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("❌ Charlie Chat: Failed to fetch providers:", error);
    // Fallback: Knowledge Base always available
    return {
      providers: {
        "knowledge-base": { available: true, name: "Knowledge Base", description: "RAG with uploaded documents" },
      },
      default: "knowledge-base",
    };
  }
};

// Send a message to Charlie AI assistant
// Skills are resolved server-side from DB assignments
export const sendMessage = async (
  content: string,
  conversationHistory: Message[] = [],
  provider: ChatProvider = "knowledge-base",
  userId?: string,
  orgId?: string
): Promise<Message> => {
  try {
    const messages = [
      ...conversationHistory
        .filter((msg) => (msg.role === "user" || msg.role === "assistant") && msg.content.trim().length > 0)
        .map((msg) => ({
          role: msg.role,
          content: msg.content,
        })),
      {
        role: "user",
        content: content,
      },
    ];

    console.log("🔵 Charlie Chat: Sending request to", CHARLIE_CHAT_ENDPOINT, "provider:", provider);

    const headers = await charlieRequestHeaders();

    const response = await fetch(CHARLIE_CHAT_ENDPOINT, {
      method: "POST",
      headers,
      body: JSON.stringify({ messages, provider }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      console.error("❌ Charlie Chat: API error:", errorMessage);
      throw new Error(errorMessage);
    }

    const data = await response.json();

    if (!data.content) {
      throw new Error("No content received from API");
    }

    console.log("✅ Charlie Chat: Received response from", data.provider || provider);

    // When a skill is applied: rawContent = original provider response, content = skill-enhanced
    // When no skill: rawContent is not set, content = provider response as-is
    const skillApplied = data.skillApplied === true;
    const rawApiContent: string = data.rawContent || data.content;
    const enhancedContent: string = data.content;

    const { content: parsedContent, followUpQuestions } = parseFollowUpQuestions(enhancedContent);
    const { content: parsedRaw } = parseFollowUpQuestions(rawApiContent);

    return {
      id: crypto.randomUUID(),
      role: "assistant",
      content: parsedContent,
      rawContent: skillApplied ? parsedRaw : enhancedContent,
      timestamp: new Date(),
      sources: data.sources || ["Knowledge Base"],
      provider: data.provider || provider,
      followUpQuestions,
      skillApplied,
      skillName: data.skillName ?? null,
    };
  } catch (error) {
    console.error("❌ Charlie Chat: Error sending message:", error);

    return {
      id: crypto.randomUUID(),
      role: "assistant",
      content:
        "I'm sorry, I'm having trouble connecting right now. Please try again in a moment. If the problem persists, please contact support.",
      timestamp: new Date(),
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

// Parse follow-up questions from the LLM response
export const parseFollowUpQuestions = (
  rawContent: string
): { content: string; followUpQuestions: string[] } => {
  const markerIndex = rawContent.lastIndexOf(FOLLOWUP_MARKER);
  if (markerIndex === -1) {
    return { content: rawContent.trim(), followUpQuestions: [] };
  }

  const content = rawContent.substring(0, markerIndex).trim();
  const endIndex = rawContent.indexOf(FOLLOWUP_END, markerIndex);
  if (endIndex === -1) {
    return { content: rawContent.trim(), followUpQuestions: [] };
  }

  const jsonPart = rawContent
    .substring(markerIndex + FOLLOWUP_MARKER.length, endIndex)
    .trim();

  try {
    const questions = JSON.parse(jsonPart);
    if (Array.isArray(questions)) {
      return { content, followUpQuestions: questions.slice(0, 3).map(String) };
    }
  } catch {
    // Parsing failed, return content as-is
  }

  return { content: content || rawContent.trim(), followUpQuestions: [] };
};

type SkillEnhancedData = {
  content: string;
  rawContent: string;
  skillApplied: boolean;
  skillName: string;
};

type StreamDoneData = {
  response_time?: number;
  query_mode?: string;
  skillApplied?: boolean;
  skillName?: string | null;
};

// Streaming chat via SSE
export const sendMessageStream = async (
  content: string,
  conversationHistory: Message[] = [],
  onToken: (token: string) => void,
  onCitations: (sources: string[]) => void,
  onDone: (meta: StreamDoneData) => void,
  onError: (error: string) => void,
  signal?: AbortSignal,
  onSkillEnhanced?: (data: SkillEnhancedData) => void,
  orgId?: string,
  provider: ChatProvider = "knowledge-base",
  _userId?: string
): Promise<void> => {
  const messages = [
    ...conversationHistory
      .filter((msg) => (msg.role === "user" || msg.role === "assistant") && msg.content.trim().length > 0)
      .map((msg) => ({ role: msg.role, content: msg.content })),
    { role: "user", content },
  ];

  const headers = await charlieRequestHeaders();

  const response = await fetch(CHARLIE_CHAT_STREAM_ENDPOINT, {
    method: "POST",
    headers,
    body: JSON.stringify({ messages, provider }),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    onError(errorData.message || `HTTP ${response.status}`);
    return;
  }

  if (!response.body) {
    onError("No response body for streaming");
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      let eventType = "";
      for (const line of lines) {
        if (line.startsWith("event: ")) {
          eventType = line.slice(7).trim();
        } else if (line.startsWith("data: ")) {
          const data = line.slice(6);
          if (eventType === "token") {
            try {
              onToken(JSON.parse(data) as string);
            } catch {
              onToken(data);
            }
          } else if (eventType === "citations" || eventType === "sources") {
            try {
              const parsed = JSON.parse(data);
              onCitations(Array.isArray(parsed) ? parsed : []);
            } catch {
              onCitations([]);
            }
          } else if (eventType === "skill_enhanced") {
            try {
              const parsed = JSON.parse(data) as SkillEnhancedData;
              onSkillEnhanced?.(parsed);
            } catch {
              // Ignore parse errors
            }
          } else if (eventType === "done") {
            try {
              const meta = JSON.parse(data) as StreamDoneData;
              onDone(meta);
            } catch {
              onDone({});
            }
          } else if (eventType === "error") {
            onError(data);
          }
          eventType = "";
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
};

// Session persistence (sessionStorage — cleared on tab close)
export const saveChatSession = (messages: Message[], provider: ChatProvider): void => {
  const session: ChatSession = {
    messages,
    provider,
    createdAt: Date.now(),
  };
  try {
    sessionStorage.setItem(CHAT_SESSION_KEY, JSON.stringify(session));
  } catch {
    // Storage full or unavailable — ignore
  }
};

export const loadChatSession = (): ChatSession | null => {
  try {
    const stored = sessionStorage.getItem(CHAT_SESSION_KEY);
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    if (
      !parsed ||
      typeof parsed !== "object" ||
      !Array.isArray((parsed as Record<string, unknown>).messages)
    ) {
      return null;
    }
    const raw = parsed as ChatSession;
    // Migrate legacy provider values from previous versions
    const migratedProvider: ChatProvider =
      (raw.provider as string) === "openai" ? "knowledge-base" : raw.provider;
    // Rehydrate Date objects immutably
    return {
      ...raw,
      provider: migratedProvider,
      messages: raw.messages.map((m) => ({
        ...m,
        timestamp: new Date(m.timestamp),
      })),
    };
  } catch {
    return null;
  }
};

export const clearChatSession = (): void => {
  sessionStorage.removeItem(CHAT_SESSION_KEY);
};
